/**
 * Listado de contratos.
 *
 * La columna que decide el trabajo comercial no es la fecha de fin: es el
 * **preaviso**. Un contrato que vence en 90 días con 60 de preaviso no
 * corre prisa; uno que vence en 45, sí, y si se pasa el plazo la
 * renovación tácita ata otro año.
 */

import { Link, useSearchParams } from 'react-router-dom';
import { DataTable, type Column } from '../table/data-table.js';
import { useListing } from '../table/use-listing.js';

interface ContractRow {
  id: string;
  code: string;
  name: string;
  customer_name: string;
  status: string;
  start_date: string;
  end_date: string | null;
  auto_renew: boolean;
  renewal_notice_days: number;
  billing_model: string;
  billing_periodicity: string;
  days_to_end: number | null;
  in_notice_window: boolean;
}

const STATUS: Record<string, { label: string; tone: string; icon: string }> = {
  DRAFT: { label: 'Borrador', tone: 'off', icon: '•' },
  ACTIVE: { label: 'Activo', tone: 'ok', icon: '✓' },
  SUSPENDED: { label: 'Suspendido', tone: 'warn', icon: '⏸' },
  EXPIRED: { label: 'Vencido', tone: 'bad', icon: '⚠' },
  CANCELLED: { label: 'Cancelado', tone: 'off', icon: '✕' },
  UNKNOWN: { label: 'Sin clasificar', tone: 'off', icon: '?' },
};

const BILLING: Record<string, string> = {
  FLAT: 'Cuota fija',
  PER_EQUIPMENT: 'Por equipo',
  PER_INSPECTION: 'Por revisión',
  PER_VISIT: 'Por visita',
  TIME_AND_MATERIALS: 'Horas y materiales',
  MIXED: 'Mixto',
};

const PERIODICITY: Record<string, string> = {
  MONTHLY: 'Mensual',
  QUARTERLY: 'Trimestral',
  BIANNUAL: 'Semestral',
  ANNUAL: 'Anual',
};

const COLUMNS: Column<ContractRow>[] = [
  {
    id: 'code',
    label: 'Código',
    // El código abre la ficha: es donde están las condiciones y la firma.
    render: (r) => (
      <Link className="table__link" to={`/contratos/${r.id}`}>
        {r.code}
      </Link>
    ),
    sortable: true,
    width: '8rem',
  },
  { id: 'name', label: 'Contrato', render: (r) => r.name },
  { id: 'customerName', label: 'Cliente', render: (r) => r.customer_name },
  {
    id: 'status',
    label: 'Estado',
    width: '9rem',
    sortable: true,
    render: (r) => {
      const s = STATUS[r.status] ?? STATUS['UNKNOWN']!;
      return <span className={`pill pill--${s.tone}`}>{`${s.icon} ${s.label}`}</span>;
    },
    exportValue: (r) => (STATUS[r.status] ?? STATUS['UNKNOWN']!).label,
  },
  {
    id: 'billingModel',
    label: 'Facturación',
    render: (r) => `${BILLING[r.billing_model] ?? r.billing_model} · ${PERIODICITY[r.billing_periodicity] ?? ''}`,
    exportValue: (r) => BILLING[r.billing_model] ?? r.billing_model,
  },
  { id: 'startDate', label: 'Inicio', render: (r) => r.start_date, sortable: true, width: '8rem' },
  {
    id: 'endDate',
    label: 'Vencimiento',
    width: '15rem',
    render: (r) => {
      if (r.end_date === null) return <span className="due--none">Indefinido</span>;

      // Fuera de la ventana de preaviso la fecha es sólo información; dentro
      // es una tarea con plazo.
      if (r.in_notice_window && r.status === 'ACTIVE') {
        return (
          <span className="due due--soon">
            ◷ Preaviso · {r.days_to_end} d · {r.end_date}
          </span>
        );
      }
      if ((r.days_to_end ?? 1) < 0) {
        return <span className="due due--late">⚠ Vencido · {r.end_date}</span>;
      }
      return <span className="due">{r.end_date}</span>;
    },
    exportValue: (r) => r.end_date ?? 'Indefinido',
  },
  {
    id: 'autoRenew',
    label: 'Renovación',
    width: '11rem',
    render: (r) =>
      r.auto_renew ? (
        // La renovación tácita es la que ata otro año si nadie avisa: se
        // dice con el número de días, no con un «sí».
        <span className="pill pill--info">↻ Tácita · preaviso {r.renewal_notice_days} d</span>
      ) : (
        <span className="pill pill--off">• Manual</span>
      ),
    exportValue: (r) => (r.auto_renew ? `Tácita, preaviso ${r.renewal_notice_days} días` : 'Manual'),
  },
];

const COLUMN_IDS = COLUMNS.map((c) => c.id);

const QUICK = [
  { id: 'active', label: 'Vigentes', param: { activeOnly: 'true' } },
  { id: 'notice', label: 'En preaviso', param: { inNoticeWindow: 'true' } },
  { id: 'all', label: 'Todos', param: {} },
] as const;

export function ContractsScreen(): React.ReactNode {
  const [params] = useSearchParams();
  const quick = QUICK.find((q) => q.id === params.get('v')) ?? QUICK[0];

  const listing = useListing<ContractRow>({
    path: '/contracts',
    columnIds: COLUMN_IDS,
    extraParams: quick.param,
    preserve: ['v'],
  });

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Contratos</h1>
        <div className="chips" role="group" aria-label="Filtros rápidos">
          {QUICK.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`chip${quick.id === option.id ? ' chip--on' : ''}`}
              aria-pressed={quick.id === option.id}
              onClick={() => listing.setParam('v', option.id === 'active' ? null : option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <Link className="button button--primary button--small" to="/contratos/nuevo">
          Nuevo contrato
        </Link>
        {listing.total !== null && <span className="screen__count">{listing.total} contratos</span>}
      </header>

      {listing.error !== null && (
        <p className="screen__error" role="alert">
          {listing.error}
        </p>
      )}

      <DataTable
        columns={COLUMNS}
        rows={listing.rows}
        idOf={(r) => r.id}
        state={listing.state}
        onStateChange={listing.setState}
        exportName={quick.id === 'notice' ? 'contratos-en-preaviso' : 'contratos'}
        loading={listing.loading}
        hasMore={listing.hasMore}
        onLoadMore={listing.loadMore}
        totalMatching={listing.total}
        emptyMessage={
          quick.id === 'notice'
            ? 'Ningún contrato dentro de su ventana de preaviso.'
            : 'No hay contratos que coincidan.'
        }
      />
    </section>
  );
}
